import axios from "axios";
import { toast } from "react-toastify";
import { setupActivityInterceptor } from "./utils/activityInterceptor";
import { setupRetryInterceptor } from "./utils/axiosRetry";

const rawBaseURL = process.env.REACT_APP_BASE_URL || "";
const baseURL = rawBaseURL.endsWith("/") ? rawBaseURL.slice(0, -1) : rawBaseURL;

const authInstance = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies
});

authInstance.interceptors.request.use(
  (config) => {
    // Add Authorization header from sessionStorage as fallback to cookies
    // This ensures immediate requests after login work before cookies are sent
    const token = sessionStorage.getItem("token");
    if (token && !config.url?.includes("/auth/")) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // Check if it's a file upload and adjust headers
    if (
      config.data instanceof FormData ||
      (config.headers["Content-Type"] && config.headers["Content-Type"].includes("multipart/form-data"))
    ) {
      // Let browser set correct multipart boundaries
      config.headers["Content-Type"] = "multipart/form-data";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're already redirecting to avoid multiple redirects
let isRedirecting = false;

// Logout helper
const handleLogout = () => {
    if (!isRedirecting) {
        isRedirecting = true;

        // Clear local storage (user data) - tokens are cleared by backend cookies or browser
        sessionStorage.removeItem('user'); // Keep for safety if used elsewhere
        localStorage.clear();

        // Show notification to user
        toast.error('Session expired. Please login again.', {
          position: 'top-right',
          autoClose: 3000,
        });

        // Preserve current path for redirect after re-login
        const currentPath = window.location.pathname;
        const isAuthPage = currentPath.startsWith('/signin') ||
                          currentPath.startsWith('/signup') ||
                          currentPath.startsWith('/forgetpassword') ||
                          currentPath.startsWith('/resetpassword');

        // Small delay to ensure toast is shown
        setTimeout(() => {
          if (!isAuthPage) {
            window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`;
          } else {
            window.location.href = '/signin';
          }
          // Reset the flag after redirect
          setTimeout(() => { isRedirecting = false; }, 1000);
        }, 100);
    }
};

authInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle CORS and Network Errors (no response object)
    if (!error.response) {
      if (error.message === 'Network Error') {
        const isDevelopment = process.env.NODE_ENV === 'development';

        if (isDevelopment) {
          console.error('Network Error Details:', {
            url: error.config?.url,
            method: error.config?.method,
            message: 'Possible CORS issue or backend not running',
            suggestion: 'Check if backend is running on http://localhost:5001'
          });
        }

        toast.error(
          'Network connection error. Please check your internet connection or try again later.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Request timeout. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry if the failed request was already for auth (login/refresh)
      if (originalRequest.url.includes('/auth/signin') || originalRequest.url.includes('/auth/refresh')) {
        // If login or refresh failed, force logout
        if (!originalRequest.url.includes('/auth/signin')) {
             handleLogout();
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        // Attempt to refresh the token using a clean axios instance to avoid interceptor loops
        await axios.post(`${baseURL}/auth/refresh`, {}, { withCredentials: true });

        // If successful, retry the original request
        return authInstance(originalRequest);
      } catch (refreshError) {
        // If refresh fails, logout
        handleLogout();
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      toast.error('Access forbidden. You do not have permission to access this resource.', {
        position: 'top-right',
        autoClose: 4000,
      });
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        console.error('404 Not Found:', error.config?.url);
      }
    }

    return Promise.reject(error);
  }
);

// Setup activity logging interceptor
setupActivityInterceptor(authInstance);

// Setup retry interceptor with exponential backoff
// Automatically retries failed requests for:
// - Network errors
// - Server errors (5xx)
// - Rate limit errors (429)
// - Timeouts
// Only retries safe methods (GET, PUT, DELETE) by default
setupRetryInterceptor(authInstance, {
  maxRetries: 3,
  baseDelay: 1000,
  retryOnlyIdempotent: true,
});

export default authInstance;
